import { useId, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoadingSpinner, Modal, PageContainer, QueryErrorAlert } from '@shared/components';
import { useAccessRefusalWatcher } from '../../hooks/useAccessRefusalWatcher';
import { useAgreementAccess } from '../../hooks/useAgreementAccess';
import { useCallerConfirmationDetails, useRecordAccessMutation } from '../../hooks/useCallerConfirmation';
import type { AccessPromptFormValues } from '../../schemas/accessPrompt.schema';
import {
    isConfirmationAlreadyRecorded,
    mapConfirmationSubmitError,
    type ConfirmationSubmitError,
} from '../../utils/callerConfirmation';
import { AccessPrompt } from './AccessPrompt';

interface Props {
    children: ReactNode;
    /** Set on a FULL_WIDTH_PAGE route: the gate's own states then sit in a PageContainer. */
    fullWidthPage?: boolean;
}

export const CALLER_NOT_CONFIRMED_RETURN_PATH = '/agreements';
export const CALLER_NOT_CONFIRMED_NOTICE =
    "The customer's details were not confirmed. The Agreement was not opened and the attempt has been recorded.";

/** Convenience only: the agreements-service gate is the boundary, this avoids requests it would refuse. */
export function AgreementAccessGate({ children, fullWidthPage }: Props) {
    const { id } = useParams<{ id: string }>();
    if (!id) return <>{children}</>;

    return (
        <AccessGate key={id} agreementId={id} fullWidthPage={fullWidthPage}>
            {children}
        </AccessGate>
    );
}

const keepOpen = () => {};

function AccessGate({ agreementId, children, fullWidthPage }: Props & { agreementId: string }) {
    const headingId = useId();
    const headingRef = useRef<HTMLHeadingElement>(null);
    const navigate = useNavigate();
    const access = useAgreementAccess(agreementId);
    const isPrompting = access.state === 'required';
    // Called here, not in the prompt, so the details are not read again when it moves into the Modal.
    const details = useCallerConfirmationDetails(agreementId, isPrompting);
    const { mutateAsync: recordAccess } = useRecordAccessMutation(agreementId);
    useAccessRefusalWatcher(agreementId);

    // Once the page has mounted it stays mounted, so an expiry never discards a draft.
    const [hasBeenGranted, setHasBeenGranted] = useState(false);
    if (access.state === 'granted' && !hasBeenGranted) setHasBeenGranted(true);

    const handleSubmit = async (values: AccessPromptFormValues): Promise<ConfirmationSubmitError | null> => {
        try {
            const { result } = await recordAccess(values);
            if (result.outcome === 'failed') {
                toast.warning(CALLER_NOT_CONFIRMED_NOTICE, { toastId: 'caller-not-confirmed' });
                navigate(CALLER_NOT_CONFIRMED_RETURN_PATH, { replace: true });
            }
            return null;
        } catch (error) {
            // An outcome already on record is followed through the re-read access state, not shown as an error.
            return isConfirmationAlreadyRecorded(error) ? null : mapConfirmationSubmitError(error);
        }
    };

    const renderPrompt = (isExpired: boolean) => (
        <AccessPrompt
            headingId={headingId}
            headingRef={headingRef}
            isExpired={isExpired}
            details={details.data}
            isLoading={details.isPending || details.isFetching || details.isAwaitingAccessRecheck}
            isError={details.isError && !details.isFetching && !details.isAwaitingAccessRecheck}
            onRetry={() => void details.refetch()}
            onSubmit={handleSubmit}
        />
    );

    const framed = (content: ReactNode) => (fullWidthPage ? <PageContainer>{content}</PageContainer> : content);

    if (hasBeenGranted || access.state === 'granted') {
        return (
            <>
                {children}
                <Modal
                    isOpen={isPrompting}
                    onClose={keepOpen}
                    showCloseButton={false}
                    closeOnBackdropClick={false}
                    backdrop="dim"
                    ariaLabelledBy={headingId}
                    initialFocusRef={headingRef}
                    className="max-w-lg p-6"
                >
                    {isPrompting && renderPrompt(true)}
                </Modal>
            </>
        );
    }

    if (access.state === 'checking') {
        return framed(<LoadingSpinner message="Checking access" className="p-4" />);
    }

    if (access.state === 'error') {
        return framed(
            <QueryErrorAlert
                message="Agreement not found, or your access to it could not be checked."
                onRetry={access.retry}
            />,
        );
    }

    return framed(
        <section
            aria-labelledby={headingId}
            className="mx-auto max-w-lg rounded-xl border border-mp-border bg-mp-surface p-6"
        >
            {renderPrompt(false)}
        </section>,
    );
}
